import { z } from 'zod';
import type {
  BaseParameterDetailAddition,
  BaseSessionDetailAddition,
  BaseSqlDetailAddition,
} from '../types';
import { nullishAsUndefined, nullishToEmpty, zodSafeParseNumber } from './utils';

export const sessionDetailSchema = z
  .object({
    instanceId: z.string({ required_error: 'Instance ID' }).min(1, 'Instance ID'),
    sid: nullishAsUndefined(z.coerce.number().optional()),
    serial: nullishAsUndefined(z.coerce.number().optional()),
    serialNumber: nullishAsUndefined(z.coerce.number().optional()),
    fromTime: nullishAsUndefined(z.string().optional()),
    toTime: nullishAsUndefined(z.string().optional()),
    isOpenedFromPA: nullishAsUndefined(z.boolean().optional()),
    init: nullishAsUndefined(z.boolean().optional()),
  })
  .transform(({ sid, serial, serialNumber, ...rest }) => ({
    ...rest,
    sid,
    serialNumber: serial ?? serialNumber ?? 0,
  }))
  .pipe(
    z.object({
      instanceId: z.string().min(1, 'Instance ID'),
      sid: z.number({ required_error: 'SID' }),
      serialNumber: z.number(),
      fromTime: nullishAsUndefined(z.string().optional()),
      toTime: nullishAsUndefined(z.string().optional()),
      isOpenedFromPA: nullishAsUndefined(z.boolean().optional()),
      init: nullishAsUndefined(z.boolean().optional()),
    }),
  );

export const sqlDetailSchema = z.object({
  sqlId: z.string({ required_error: 'SQL ID' }).min(1, 'SQL ID'),
  instanceId: z.string({ required_error: 'Instance ID' }).min(1, 'Instance ID'),
  fromTime: nullishAsUndefined(z.string().optional()),
  toTime: nullishAsUndefined(z.string().optional()),
  schema: nullishAsUndefined(z.string().optional()),
  module: nullishAsUndefined(z.string().optional()),
  planHashValue: zodSafeParseNumber,
  childNumber: zodSafeParseNumber,
  programId: nullishAsUndefined(z.number().optional()),
  programLine: nullishAsUndefined(z.number().optional()),
});

export const parameterDetailSchema = z
  .object({
    instanceId: z.string({ required_error: 'Instance ID' }).min(1, 'Instance ID'),
    name: nullishAsUndefined(z.string().optional()),
    parameterName: nullishAsUndefined(z.string().optional()),
    fromTime: nullishAsUndefined(z.string().optional()),
    toTime: nullishAsUndefined(z.string().optional()),
  })
  .transform(({ name, parameterName, ...rest }) => ({
    ...rest,
    parameterName: name ?? parameterName,
  }))
  .pipe(
    z.object({
      parameterName: z.string({ required_error: 'Parameter Name' }).min(1, 'Parameter Name'),
      instanceId: z.string().min(1, 'Instance ID'),
      fromTime: z.string().optional(),
      toTime: z.string().optional(),
    }),
  );

export const documentTextSchema = z.object({
  sqlId: z.preprocess(nullishToEmpty(''), z.string({ required_error: 'SQL ID' }).min(1, 'SQL ID')),
  instanceId: z.preprocess(
    nullishToEmpty(''),
    z.string({ required_error: 'Instance ID' }).min(1, 'Instance ID'),
  ),
});

export type SessionDetailSchema = z.infer<typeof sessionDetailSchema>;
export type SqlDetailSchema = z.infer<typeof sqlDetailSchema>;
export type ParameterDetailSchema = z.infer<typeof parameterDetailSchema>;
export type DocumentTextSchema = z.infer<typeof documentTextSchema>;

export type OracleSessionDetailAddition = BaseSessionDetailAddition & SessionDetailSchema;
export type OracleSqlDetailAddition = BaseSqlDetailAddition & SqlDetailSchema;
export type OracleParameterDetailAddition = Omit<
  BaseParameterDetailAddition & ParameterDetailSchema,
  'parameterName'
>;
